import * as ko from 'knockout';
import LZString from 'lz-string';
import { ppFetch } from '../shared/pp-fetch';
import { SchedulePickerBase } from '../shared/schedule-picker-base';
import { MlbTeam } from './models/team';
import { MlbGame } from './models/game';
import { MlbWeek } from './models/week';
import { SelectionType } from '../shared/enums/selection-type';
import { RankMethod } from '../shared/enums/rank-method';
import { Conferences } from './enums';
import { Enumerable } from '../shared/linq';
import { userConfigManager } from '../shared/user-config-manager';
import { readConfig, getApimBaseUrl, getWebAppBaseUrl } from '../shared/config';
import { formatMonthOrdinalDay } from '../shared/date-helpers';
import type { IGame } from '../shared/interfaces/IGame';
import * as Information from './schedule-info';
import * as BracketGenerator from './bracket-generator';
import type { MlbTiebreakerTeam, GameResult } from '../shared/tiebreaker/types';
import { mlbConferenceRanker, mlbDivisionPickTop } from './tiebreaker/mlb-tiebreaker';
import { ArrayGameResultsLookup } from '../shared/tiebreaker/game-results-lookup';
import { calcPct, calcPctNaN } from '../shared/tiebreaker/helpers';
import { toMlbTiebreakerTeam, buildTeamObsMap } from '../shared/tiebreaker/adapters/ko-adapter';
import { mapDictionaryToArray, rankSort } from '../shared/helpers';
import { Series } from '../shared/models/series';
import { getMlbDivisionWinnerState } from './division-winner-state';

// Re-export for entry point use
export { MlbApp };

// Expose static helpers on window for legacy Knockout template bindings
function exposeToWindow(app: MlbApp): void {
  (window as any).SchedulePicker = (window as any).SchedulePicker ?? {};
  (window as any).SchedulePicker.MLB = (window as any).SchedulePicker.MLB ?? {};
  (window as any).SchedulePicker.MLB.App = {
    mapDictionaryToArray: mapDictionaryToArray,
    RankSort: rankSort,
  };
}

// ─── Types ──────────────────────────────────────────────────────────────────

type MlbTeamObs = KnockoutObservable<MlbTeam>;
type GameScheduleMap = Record<number, KnockoutObservable<IGame>>;
type ConferenceTeamDictionary = Record<string, Record<string, MlbTeamObs>>;
type TeamDictionary = Record<string, Record<string, Record<string, MlbTeamObs>>>;

class MlbApp extends SchedulePickerBase {
  ConferenceTeams: ConferenceTeamDictionary;
  Teams: { key: string; value: Record<string, Record<string, MlbTeamObs>> }[];
  Dict: TeamDictionary;
  WeekDateMap: any;
  GameScheduleGameMap: GameScheduleMap = {};
  ALTop: KnockoutComputed<MlbTeamObs[]>;
  NLTop: KnockoutComputed<MlbTeamObs[]>;
  RMLBStyles: KnockoutObservable<boolean>;
  Warning: KnockoutObservable<string>;
  SkipDivisionRank: KnockoutObservable<boolean>;
  SeasonYearEnd = 2017;
  HtmlExport: KnockoutObservable<any>;
  Export: KnockoutObservable<any>;
  NumGames: KnockoutObservable<number>;
  ShowDivision: KnockoutObservable<boolean>;
  HideStatus: KnockoutObservable<boolean>;
  Hash: KnockoutObservable<string>;
  StickyColumns: KnockoutObservable<boolean>;
  static readonly postSeasonWeeks: number = 4;

  Timeout: ReturnType<typeof setTimeout> | undefined;

  constructor() {
    const config = readConfig();
    super('baseball', config);
    const self = this;

    self.Hash = ko.observable(window.location.hash);

    self.HideStatus = ko.observable(userConfigManager.UserConfig.MLB?.HideStatus ?? false);
    self.HideStatus.subscribe((newVal: boolean) => {
      if (userConfigManager.UserConfig.MLB && newVal !== userConfigManager.UserConfig.MLB.HideStatus) {
        userConfigManager.UserConfig.MLB.HideStatus = newVal;
        userConfigManager.saveStorage();
      }
    });

    self.StickyColumns = ko.observable(userConfigManager.UserConfig.MLB?.StickyColumns ?? false);
    if (self.StickyColumns()) {
      document.querySelectorAll('footer div.row > div').forEach(el => {
        el.classList.add('col-sm-push-2', 'col-sm-9');
        el.classList.remove('col-sm-10');
      });
    } else {
      document.querySelectorAll('footer div.row > div').forEach(el => {
        el.classList.remove('col-sm-push-2');
      });
    }
    self.StickyColumns.subscribe((newVal: boolean) => {
      if (newVal) {
        document.querySelectorAll('footer div.row > div').forEach(el => {
          el.classList.add('col-sm-push-2');
        });
      } else {
        document.querySelectorAll('footer div.row > div').forEach(el => {
          el.classList.remove('col-sm-push-2');
        });
      }
      if (userConfigManager.UserConfig.MLB && newVal !== userConfigManager.UserConfig.MLB.StickyColumns) {
        userConfigManager.UserConfig.MLB.StickyColumns = newVal;
        userConfigManager.saveStorage();
      }
    });

    self.ShowDivision = ko.observable(userConfigManager.UserConfig.MLB?.DivToggle ?? true);
    self.NumberOfTeams(30);
    self.Warning = ko.observable<string>('');
    self.HtmlExport = ko.observable('');
    self.Export = ko.observable('');
    self.EmptySeasonString = 'Aw18ZXTt-DFOS1b0c17Pd-wYUcSaWeRZVdTbXfQ408y62+x519z73-wMFDhI0WPET8QA';
    self.NumGames = ko.observable(0);

    super.startComputing();

    self.SaveString.subscribe(() => {
      self.setExport();
    });

    const handleLeagueData = (
      leagueName: string,
      seasonYear: number,
      currentWeekString: string,
      alTeams: ITeamData[],
      nlTeams: ITeamData[],
      leagueGames: IGameData[],
      replaceTeams: any[],
    ) => {
      if (seasonYear) { self.SeasonYearEnd = seasonYear; }
      if (currentWeekString) { self.CurrentSeasonString(currentWeekString); }
      self.LeagueName(leagueName);

      const dict: TeamDictionary = {};
      let teamObj: MlbTeamObs;
      self.ConferenceTeams = {} as ConferenceTeamDictionary;
      self.ConferenceTeams['AL'] = {} as Record<string, MlbTeamObs>;
      dict['AL'] = {} as Record<string, Record<string, MlbTeamObs>>;
      ko.utils.arrayForEach(alTeams, (team: ITeamData) => {
        dict['AL'][team.Division] = dict['AL'][team.Division] || {};
        teamObj = ko.observable(new MlbTeam(team.TeamAbbreviation, team.RegionName, team.TeamName, team.Conference, team.Division, team.TeamID, team.TeamLogo));
        dict['AL'][team.Division][team.TeamAbbreviation] = teamObj;
        self.ConferenceTeams['AL'][team.TeamAbbreviation] = teamObj;
      });
      dict['NL'] = {} as Record<string, Record<string, MlbTeamObs>>;
      self.ConferenceTeams['NL'] = {} as Record<string, MlbTeamObs>;
      ko.utils.arrayForEach(nlTeams, (team: ITeamData) => {
        teamObj = ko.observable(new MlbTeam(team.TeamAbbreviation, team.RegionName, team.TeamName, team.Conference, team.Division, team.TeamID, team.TeamLogo));
        dict['NL'][team.Division] = dict['NL'][team.Division] || {};
        dict['NL'][team.Division][team.TeamAbbreviation] = teamObj;
        self.ConferenceTeams['NL'][team.TeamAbbreviation] = teamObj;
      });
      self.Dict = dict;
      self.Teams = mapDictionaryToArray(self.Dict);
      self.Weeks = Information.GetSchedule(self.Dict, leagueGames);
      self.NumGames(leagueGames.length);
      self.MaxWeek(Enumerable.From(leagueGames).Max((g: any) => g.Week));
      self.MaxWeek.valueHasMutated();
      self.MaxWeek.notifySubscribers();
      self.TotalWeeks = self.MaxWeek() + MlbApp.postSeasonWeeks;
      self.WeekDateMap = {} as Record<number, any>;

      for (const weekIdx in self.Weeks) {
        self.WeekDateMap[weekIdx] = self.WeekDateMap[weekIdx] || {};
        const week = self.Weeks[weekIdx];
        let games = (week[0] as MlbWeek).games().concat((week[1] as MlbWeek).games());
        if (week.length === 3) {
          games = games.concat((week[2] as MlbWeek).games());
        }
        const orderedGames = Enumerable.From(games)
          .Where((p: any) => p != undefined)
          .OrderBy((p: KnockoutObservable<IGame>) => p().SortOrder());
        const wIdx = weekIdx;
        if (orderedGames.Any()) {
          self.WeekDateMap[wIdx].DayString = ko.computed(() =>
            `Week ${wIdx}: ${formatMonthOrdinalDay(orderedGames.First()().gameDate())} - ${formatMonthOrdinalDay(orderedGames.Last()().gameDate())}`
          );
        } else {
          self.WeekDateMap[wIdx].DayString = ko.computed(() => `Week ${wIdx}`);
        }
        for (const gameObs of games) {
          const game = gameObs();
          self.GameScheduleGameMap[game.gameScheduleNum()] = gameObs;
        }
      }

      // Post-season week entries (may not exist in self.Weeks yet)
      for (let i = 1; i <= MlbApp.postSeasonWeeks; i++) {
        self.WeekDateMap[self.MaxWeek() + i] = self.WeekDateMap[self.MaxWeek() + i] || {};
      }
      self.WeekDateMap[self.MaxWeek() + 1].DayString = ko.computed(() => 'Wildcard');
      self.WeekDateMap[self.MaxWeek() + 2].DayString = ko.computed(() => 'Division Series');
      self.WeekDateMap[self.MaxWeek() + 3].DayString = ko.computed(() => 'Championship Series');
      self.WeekDateMap[self.MaxWeek() + 4].DayString = ko.computed(() => 'World Series');

      self.SelectedWeek({ key: 1, value: self.Weeks[1] });
      self.NumGames(leagueGames.length);

      self.Warning.subscribe((newVal) => {
        if (newVal) {
          document.querySelectorAll('.alert').forEach(el => {
            (el as any).alert?.();
          });
        }
      });

      self.ALTop = ko.computed(() => {
        return Enumerable.From(self.AllTeams())
          .Where((p: any) => p().Conference === 'AL')
          .OrderBy((p: any) => p().ConferenceRank())
          .Take(6)
          .ToArray();
      });
      self.NLTop = ko.computed(() => {
        return Enumerable.From(self.AllTeams())
          .Where((p: any) => p().Conference === 'NL')
          .OrderBy((p: any) => p().ConferenceRank())
          .Take(6)
          .ToArray();
      });

      self.RMLBStyles = ko.observable<boolean>(false);
      self.RMLBStyles.subscribe(() => {
        self.setExport();
      });
      self.SkipDivisionRank = ko.observable<boolean>(false);
      self.init(replaceTeams);

      if (window.location.hash) {
        const teamName = window.location.hash.slice(1);
        const searchTeam = self.findTeam(teamName);
        if (searchTeam) {
          self.SelectedTeam(searchTeam());
        }
      }

      self.UrlString.subscribe((newString) => {
        if (newString && newString.length) {
          newString = newString.replace(/%20/gi, '+');
          const decompress = LZString.decompressFromEncodedURIComponent(newString);
          if (decompress) {
            self.load(decompress);
            if (
              self.Weeks[self.TotalWeeks] &&
              (self.Weeks[self.TotalWeeks][1] as MlbWeek).GamesComputed().length > 0 &&
              (self.Weeks[self.TotalWeeks][1] as MlbWeek).GamesComputed()[0]().selectionMade()
            ) {
              self.SelectedWeek({ key: self.MaxWeek() + 4, value: self.Weeks[self.MaxWeek() + 4] });
            }
          }
        } else {
          super.finishComputing();
        }
      });

      if (self.LoadedWatchlist()) {
        self.UrlString(self.LoadedWatchlist().SaveString());
      } else {
        self.UrlString(SchedulePickerBase.getQueryVariable('L') || '' || localStorage.getItem(readConfig().leagueGuid));
      }

      document.querySelectorAll('#content .hidden').forEach(el => el.classList.remove('hidden'));
      const loadingEl = document.getElementById('loading');
      if (loadingEl) { loadingEl.style.display = 'none'; }
      setTimeout(() => {}, 80);
      ko.applyBindings(self, document.getElementById('content'));

      const navbarsForPad = document.querySelectorAll('.navbar');
      const nb0 = navbarsForPad[0] as HTMLElement | undefined;
      const nb1 = navbarsForPad[1] as HTMLElement | undefined;
      const topPad = (nb0?.offsetHeight ?? 0) + (nb1?.offsetHeight ?? 0);
      document.body.style.paddingTop = topPad + 'px';

      document.querySelectorAll('[data-toggle="popover"]').forEach(el => {
        (el as any).popover?.();
      });

      self.ShowDivision.subscribe(newVal => {
        if (userConfigManager.UserConfig.MLB) {
          userConfigManager.UserConfig.MLB.DivToggle = newVal;
          userConfigManager.saveStorage();
        }
      });

      self.setGameScheduleNumbers(self.NumGames());
      super.initTeamSubscribers();
      self.setExport();
      super.finishComputing();
    };

    // Load from V2 API
    const { leagueGuid, sportId } = config;
    (window as any).leagueGuid = leagueGuid;

    ppFetch<any>({
      url: `${getApimBaseUrl()}/LeagueEngine/leagues/${sportId}/${leagueGuid}/official`,
      method: 'GET',
    }).then((result: any) => {
        const teamById: Record<number, any> = {};
        result.teams.forEach((t: any) => { teamById[t.teamId] = t; });
        const confMap: Record<string, string> = { American: 'AL', National: 'NL', AL: 'AL', NL: 'NL' };
        const alTeams: ITeamData[] = result.teams
          .filter((t: any) => (confMap[t.conference] || t.conference) === 'AL')
          .map((t: any) => ({
            TeamAbbreviation: t.teamAbbreviation,
            RegionName: t.regionName,
            TeamName: t.teamName,
            Conference: 'AL',
            Division: t.division,
            TeamID: t.teamId,
            TeamLogo: t.teamLogo,
          } as ITeamData));
        const nlTeams: ITeamData[] = result.teams
          .filter((t: any) => (confMap[t.conference] || t.conference) === 'NL')
          .map((t: any) => ({
            TeamAbbreviation: t.teamAbbreviation,
            RegionName: t.regionName,
            TeamName: t.teamName,
            Conference: 'NL',
            Division: t.division,
            TeamID: t.teamId,
            TeamLogo: t.teamLogo,
          } as ITeamData));
        const v1Games: IGameData[] = result.league.games.map((g: any) => {
          const home = teamById[g.homeId];
          const away = teamById[g.awayId];
          return {
            Home: {
              TeamAbbreviation: home.teamAbbreviation,
              RegionName: home.regionName,
              TeamName: home.teamName,
              Conference: confMap[home.conference] || home.conference,
              Division: home.division,
              TeamID: home.teamId,
              TeamLogo: home.teamLogo,
            },
            Away: {
              TeamAbbreviation: away.teamAbbreviation,
              RegionName: away.regionName,
              TeamName: away.teamName,
              Conference: confMap[away.conference] || away.conference,
              Division: away.division,
              TeamID: away.teamId,
              TeamLogo: away.teamLogo,
            },
            Week: g.week,
            GameTypeID: 0,
            GameDate: g.gameDate,
            GameDateOffset: g.gameDate,
            GameKey: g.gameKey,
            GameGUID: g.gameGuid,
            GameScheduleNum: g.gameScheduleNum,
          } as IGameData;
        });
        handleLeagueData(
          result.league.leagueName,
          result.league.superbowlYear,
          result.league.currentWeekString,
          alTeams,
          nlTeams,
          v1Games,
          [],
        );
    }).catch((err: unknown) => {
      const e = err as Error;
      console.error('MLB schedule-picker load error:', e?.message, e?.stack, err);
      const loadingErrEl = document.getElementById('loading');
      if (loadingErrEl) { loadingErrEl.textContent = `An error occurred: ${e?.message ?? String(err)}`; }
      (window as any).PlayoffPredictor?.Spin?.hide?.();
    });

    const bracketsEl = document.getElementById('brackets');
    if (bracketsEl) { bracketsEl.style.display = 'none'; }
    document.addEventListener('click', (e: MouseEvent) => {
      if ((e.target as Element).closest('#bracketUpload')) {
        const brackets = document.getElementById('brackets');
        if (!brackets) { return; }
        brackets.querySelectorAll('a').forEach(a => a.remove());
        const uploadingSpan = document.createElement('span');
        uploadingSpan.className = 'pull-right';
        uploadingSpan.textContent = 'Uploading...';
        brackets.appendChild(uploadingSpan);
        const canvas = document.querySelector('canvas') as HTMLCanvasElement | null;
        if (!canvas) { return; }
        const dataUrl = canvas.toDataURL('image/jpeg', 1.0);
        (window as any).PlayoffPredictor?.Ajax({
          url: '/baseball/CustomLeague/UploadBracket',
          type: 'POST',
          method: 'POST',
          data: {
            Base64: dataUrl,
            LeagueGuid: leagueGuid,
            SaveString: SchedulePickerBase.getQueryVariable('L') || '' || localStorage.getItem(leagueGuid),
          },
        })?.done((ajaxResult: any) => {
          brackets.querySelectorAll('span').forEach(s => s.remove());
          const bracketLink = document.createElement('a');
          bracketLink.href = `${getWebAppBaseUrl()}/${ajaxResult.Model.link}`;
          bracketLink.className = 'pull-right';
          bracketLink.textContent = 'Bracket Link';
          brackets.appendChild(bracketLink);
          const tweetText = `I just picked who I think will win next year's %23MLB Finals on %23PlayoffPredictors ${getWebAppBaseUrl()}${ajaxResult.Model.link}`;
          console.log(tweetText);
          brackets.insertAdjacentHTML(
            'beforeend',
            `<a class="twitter-share-button" href="https://x.com/intent/tweet?text=${tweetText}">Tweet</a>` +
            `<a href="https://x.com/RayLehnhoff?ref_src=twsrc%5Etfw" class="twitter-follow-button" data-show-count="false">Follow @RayLehnhoff</a>`,
          );
          (window as any).twttr?.widgets?.load(brackets);
        });
      }
    });

    console.log(self);
    exposeToWindow(self);
  }

  setExport(): void {
    const self = this;
    const htmlStr = (() => {
      let html = '<h3>AL</h3><div>';
      let conf: string, team: any, style: string;
      for (const div of mapDictionaryToArray(self.Teams[0].value)) {
        conf = `<table><thead><tr><th>#</th><th>${div.key}</th><th>WL</th><th>Div</th></tr></thead><tbody>`;
        for (const teamRef of mapDictionaryToArray(div.value).sort(rankSort)) {
          team = (teamRef.value as KnockoutObservable<any>)();
          style = team.ConferenceCss() === 'in-post-season' ? 'color:green; font-weight:bold' : 'color:red;font-style:italic;';
          conf += `<tr><td style="${style}">${team.ConferenceRankText()}</td><td><a href='${self.SaveVal() + '#' + team.TeamAbbreviation}'>${team.TeamName}</a></td><td>${team.GameRecords.AllRecord.toString()}</td><td>${team.GameRecords.DivisionRecord.toString()}</td></tr>`;
        }
        conf += '</table>';
        html += conf;
      }
      html += '</div><div><h3>NL</h3>';
      for (const div of mapDictionaryToArray(self.Teams[1].value)) {
        conf = `<table><thead><tr><th>#</th><th>${div.key}</th><th>WL</th><th>Div</th></tr></thead><tbody>`;
        for (const teamRef of mapDictionaryToArray(div.value).sort(rankSort)) {
          team = (teamRef.value as KnockoutObservable<any>)();
          style = team.ConferenceCss() === 'in-post-season' ? 'color:green; font-weight:bold' : 'color:red;font-style:italic;';
          conf += `<tr><td style="${style}">${team.ConferenceRankText()}</td><td><a href='${self.SaveVal() + '#' + team.TeamAbbreviation}'>${team.TeamName}</a></td><td>${team.GameRecords.AllRecord.toString()}</td><td>${team.GameRecords.DivisionRecord.toString()}</td></tr>`;
        }
        conf += '</table>';
        html += conf;
      }
      html += `</div><a href='${self.SaveVal()}'>Generated By The MLB Playoff Predictor</a>`;
      return html;
    })();
    self.HtmlExport(htmlStr);

    const $redditExport = (() => {
      const exportArray: any[] = [];
      let conferenceName = 'NL';
      if (self.RMLBStyles()) {
        conferenceName = '[](/NL)NL';
      }
      exportArray.push('#' + conferenceName + '\n\nRank | Team | W | L\n-|-|-:|-:|-:|\n');
      ko.utils.arrayForEach(self.NLTop(), (item: MlbTeamObs, idx: number) => {
        let teamName = '[' + item().TeamAbbreviation + '](' + self.SaveVal() + '#' + item().TeamAbbreviation + ')';
        if (self.RMLBStyles()) {
          teamName = '[](' + item().TeamAbbreviation + ')' + teamName;
        }
        exportArray.push((idx + 1), '|', teamName, '|', item().GameRecords.AllRecord.Wins(), '|', item().GameRecords.AllRecord.Losses(), '\n');
      });
      exportArray.push('\n\n');
      conferenceName = 'AL';
      if (self.RMLBStyles()) {
        conferenceName = '[](/AL)AL';
      }
      exportArray.push('#' + conferenceName + '\n\nRank | Team | W | L\n-|-|-:|-:|-:|\n');
      ko.utils.arrayForEach(self.ALTop(), (item: MlbTeamObs, idx: number) => {
        let teamName = '[' + item().TeamAbbreviation + '](' + self.SaveVal() + '#' + item().TeamAbbreviation + ')';
        if (self.RMLBStyles()) {
          teamName = '[](' + item().TeamAbbreviation + ')' + teamName;
        }
        exportArray.push((idx + 1), '|', teamName, '|', item().GameRecords.AllRecord.Wins(), '|', item().GameRecords.AllRecord.Losses(), '\n');
      });
      exportArray.push('\n\nGenerated by The [MLB Playoff Predictor](', self.SaveVal(), ')');
      return exportArray.join('');
    })();
    self.Export($redditExport);
  }

  previewHtml(): void {
    const win = window.open(
      '',
      'Title',
      'toolbar=no, location=no, directories=no, status=no, menubar=no, scrollbars=yes, resizable=yes, width=780, height=200, top=' +
        (screen.height - 400) + ', left=' + (screen.width - 840),
    );
    win.document.body.innerHTML = this.HtmlExport();
  }

  init(replaceTeams?: any[]): void {
    const self = this;
    const allTeams: MlbTeamObs[] = [];
    const ALDivisions = this.Dict['AL'];
    for (const divisionInd in ALDivisions) {
      const division = ALDivisions[divisionInd];
      for (const teamInd in division) {
        allTeams.push(division[teamInd]);
      }
    }
    const NLDivisions = this.Dict['NL'];
    for (const divisionInd in NLDivisions) {
      const division = NLDivisions[divisionInd];
      for (const teamInd in division) {
        allTeams.push(division[teamInd]);
      }
    }
    this.AllTeams(allTeams);

    if (replaceTeams) {
      for (const teamOverride of replaceTeams) {
        const teamReplaced: any = Enumerable.From(this.AllTeams()).First((p: any) => p().TeamID === teamOverride.TeamReplaced);
        this.AllTeams.remove(teamReplaced);
        teamReplaced().RegionName = teamOverride.RegionName;
        teamReplaced().TeamName = teamOverride.TeamName;
        teamReplaced().Conference = teamOverride.Conference;
        teamReplaced().Division = teamOverride.Division;
        this.AllTeams.push(teamReplaced);
      }
    }

    this.SelectedTeam.subscribe((newVal) => {
      if (newVal) {
        this.IsTeamSelected(true);
        setTimeout(() => {
          const teamAdbox = document.getElementById('team-adbox') as HTMLElement | null;
          const teamStatsTable = document.getElementById('team-statstable') as HTMLElement | null;
          if (teamAdbox && teamStatsTable) {
            teamAdbox.style.width = teamStatsTable.offsetWidth + 'px';
            teamAdbox.style.height = (teamStatsTable.offsetHeight * 3) + 'px';
            const ins = teamAdbox.querySelector('ins') as HTMLElement | null;
            if (ins) { ins.style.height = (teamAdbox.offsetHeight * 2) + 'px'; }
          }
        }, 1000);

        newVal.Schedule(this.getScheduleForTeam(newVal));
        newVal.FakeObs.valueHasMutated();
      }
    });
  }

  unselectGame(gameInput: KnockoutObservable<IGame>, skipUpdate?: boolean): void {
    const self = this;
    gameInput().selectionMade(false);
    gameInput().teamSelected('NONE');
    self.GameArray[gameInput().gameScheduleNum()] = 0;
    self.updateSaveString();
    if (!skipUpdate) {
      self.UpdateStats(-1, true);
    }
  }

  load(urlString: string): void {
    const self = this;
    super.startComputing();
    self.BlockHistoryPush(true);
    const numGames = self.NumGames();
    let biggestWeek = 1;

    if (self.IsAfterFirstLoad) {
      for (const weekInd in self.Weeks) {
        const gameType = self.Weeks[weekInd];
        ko.utils.arrayForEach(gameType, (week: any) => {
          ko.utils.arrayForEach(week.GamesComputed(), (game: KnockoutObservable<any>) => {
            game().selectionType(SelectionType.NONE);
            game().selectionMade(false);
            game().teamSelected('');
          });
          ko.utils.arrayForEach(week.Series(), (series: Series) => {
            ko.utils.arrayForEach(series.games(), (game: KnockoutObservable<IGame>) => {
              game().selectionType(SelectionType.NONE);
              game().selectionMade(false);
              game().teamSelected('');
            });
          });
        });
      }
      const bracketsLoadEl = document.getElementById('brackets');
      if (bracketsLoadEl) { bracketsLoadEl.innerHTML = ''; }

      for (let i = 0; i < self.AllTeams().length; i++) {
        const team = self.AllTeams()[i]();
        team.reset();
        team.FakeObs();
      }
      self.GameArray.fill(SelectionType.NONE, urlString.length);
    }

    for (let i = 0; i < urlString.length; i++) {
      if (i > self.NumGames() + 54) {
        continue;
      }

      const game = self.GameScheduleGameMap[i];
      if (!game || !game() || !(game() as any).away || !(game() as any).home) {
        continue;
      }
      const _game = game();
      const home = (_game as any).home();
      const away = (_game as any).away();
      const weekInd = (_game as any).week;
      const input = {
        gameGuid: _game.gameGuid,
        home: {
          Conference: home.Conference,
          Division: home.Division,
          TeamName: home.TeamName,
          Pct: home.GameRecords.AllRecord.Pct,
        },
        away: {
          Conference: away.Conference,
          Division: away.Division,
          TeamName: away.TeamName,
          Pct: away.GameRecords.AllRecord.Pct,
        },
      };

      const skipUpdate = i < numGames;
      let selection = parseInt(urlString[i], 10);
      if (!selection) {
        selection = 0;
      }

      switch (selection) {
        case SelectionType.AWAY_WIN:
          self.GameArray[i] = SelectionType.AWAY_WIN;
          if (biggestWeek < +weekInd) {
            biggestWeek = +weekInd;
          }
          if (i < self.NumGames()) {
            away.Wins.push(input as unknown as IGame);
            home.Losses.push(input as unknown as IGame);
            self.gameSelected(game, (game() as any).away, selection, i, skipUpdate);
          } else {
            self.selectSeriesGame(game, (game() as any).away, selection, true);
          }
          break;

        case SelectionType.HOME_WIN:
          self.GameArray[i] = SelectionType.HOME_WIN;
          if (biggestWeek < +weekInd) {
            biggestWeek = +weekInd;
          }
          if (i < self.NumGames()) {
            away.Losses.push(input as unknown as IGame);
            home.Wins.push(input as unknown as IGame);
            self.gameSelected(game, (game() as any).home, selection, i, skipUpdate);
          } else {
            self.selectSeriesGame(game, (game() as any).home, selection, true);
          }
          break;

        case SelectionType.TIE:
          // MLB has no ties
          break;

        case SelectionType.NONE:
          self.GameArray[i] = SelectionType.NONE;
          if (i < self.NumGames()) {
            self.unselectGame(game, skipUpdate);
          } else {
            self.selectSeriesGame(game, null, 0, true);
          }
          break;
      }

      if (i === self.NumGames() - 1) {
        super.initTeamSubscribers();
        this.UpdateStats(i, true);
      }

      if (i === 2441) {
        self.seedDV();
      } else if (i === 2461) {
        self.seedCG();
      } else if (i === 2475) {
        self.seedFinals();
      }
    }

    self.switchContext({ key: biggestWeek, value: self.Weeks[biggestWeek] });
    self.BiggestWeek(biggestWeek);

    if (self.Hash()) {
      try {
        const team = self.findTeam(self.Hash().slice(1))();
        self.showTeamStats(team);
        self.IsTeamSelected(true);
      } catch (ex) {
        console.warn(ex);
      }
    }

    self.IsAfterFirstLoad = true;
    self.BlockHistoryPush(true);
    self.pushHistory();
    self.BlockHistoryPush(false);
  }

  seedDV(): void {
    const self = this;
    const wcWeek = self.MaxWeek() + 1;
    const dvWeek = self.MaxWeek() + 2;
    const ALSeriesRecord = (this.Weeks[wcWeek][0] as MlbWeek).Series();
    const NLSeriesRecord = (this.Weeks[wcWeek][2] as MlbWeek).Series();
    const teamsEnum = Enumerable.From(self.AllTeams());
    const dvSeriesStart = 2441;

    if (ALSeriesRecord.length === 2 && ALSeriesRecord[0].seriesDecided() && ALSeriesRecord[1].seriesDecided()) {
      (this.Weeks[dvWeek][0] as MlbWeek).Series([]);
      const team1 = teamsEnum.First((p: any) => p().Conference === 'AL' && p().ConferenceRank() === 1) as any;
      this.generateSeries(this.Weeks[dvWeek][0] as MlbWeek, team1, ALSeriesRecord[1].seriesWinner() as any, dvWeek, 5, dvSeriesStart);
      const team2 = teamsEnum.First((p: any) => p().Conference === 'AL' && p().ConferenceRank() === 2) as any;
      this.generateSeries(this.Weeks[dvWeek][0] as MlbWeek, team2, ALSeriesRecord[0].seriesWinner() as any, dvWeek, 5, dvSeriesStart + 5);
      this.clearSeries((this.Weeks[dvWeek][0] as MlbWeek).Series()[0]);
      this.clearSeries((this.Weeks[dvWeek][0] as MlbWeek).Series()[1]);
    }

    if (NLSeriesRecord.length === 2 && NLSeriesRecord[0].seriesDecided() && NLSeriesRecord[1].seriesDecided()) {
      (this.Weeks[dvWeek][2] as MlbWeek).Series([]);
      const team1 = teamsEnum.First((p: any) => p().Conference === 'NL' && p().ConferenceRank() === 1) as any;
      this.generateSeries(this.Weeks[dvWeek][2] as MlbWeek, team1, NLSeriesRecord[1].seriesWinner() as any, dvWeek, 5, dvSeriesStart + 10);
      const team2 = teamsEnum.First((p: any) => p().Conference === 'NL' && p().ConferenceRank() === 2) as any;
      this.generateSeries(this.Weeks[dvWeek][2] as MlbWeek, team2, NLSeriesRecord[0].seriesWinner() as any, dvWeek, 5, dvSeriesStart + 15);
      this.clearSeries((this.Weeks[dvWeek][2] as MlbWeek).Series()[0]);
      this.clearSeries((this.Weeks[dvWeek][2] as MlbWeek).Series()[1]);
    }
  }

  seedCG(): void {
    const self = this;
    const dvWeek = self.MaxWeek() + 2;
    const cgWeek = self.MaxWeek() + 3;
    const ALSeriesRecord = (this.Weeks[dvWeek][0] as MlbWeek).Series();
    const NLSeriesRecord = (this.Weeks[dvWeek][2] as MlbWeek).Series();
    const cgStart = 2461;

    if (
      ALSeriesRecord.length === 2 &&
      ALSeriesRecord[0].seriesDecided() &&
      ALSeriesRecord[1].seriesDecided()
    ) {
      const dvTeams = this.getHomeFieldAdvantageForPlayoffs([ALSeriesRecord[0].seriesWinner(), ALSeriesRecord[1].seriesWinner()]);
      (this.Weeks[cgWeek][0] as MlbWeek).Series([]);
      this.generateSeries(this.Weeks[cgWeek][0] as MlbWeek, dvTeams[0], dvTeams[1], cgWeek, 7, cgStart);
      this.clearSeries((this.Weeks[cgWeek][0] as MlbWeek).Series()[0]);
    }

    if (
      NLSeriesRecord.length === 2 &&
      NLSeriesRecord[0].seriesDecided() &&
      NLSeriesRecord[1].seriesDecided()
    ) {
      const dvTeams = this.getHomeFieldAdvantageForPlayoffs([NLSeriesRecord[0].seriesWinner(), NLSeriesRecord[1].seriesWinner()]);
      (this.Weeks[cgWeek][2] as MlbWeek).Series([]);
      this.generateSeries(this.Weeks[cgWeek][2] as MlbWeek, dvTeams[0], dvTeams[1], cgWeek, 7, cgStart + 7);
      this.clearSeries((this.Weeks[cgWeek][2] as MlbWeek).Series()[0]);
    }
  }

  seedFinals(): void {
    const self = this;
    const cgWeek = self.MaxWeek() + 3;
    const cfWeek = self.MaxWeek() + 4;
    const ALSeriesRecord = (this.Weeks[cgWeek][0] as MlbWeek).Series();
    const NLSeriesRecord = (this.Weeks[cgWeek][2] as MlbWeek).Series();
    if (!(ALSeriesRecord.length && NLSeriesRecord.length)) {
      return;
    }
    if (ALSeriesRecord[0].seriesDecided() && NLSeriesRecord[0].seriesDecided()) {
      (this.Weeks[cfWeek][1] as MlbWeek).Series([]);
      const dvTeams = this.getHomeFieldAdvantageForFinals([ALSeriesRecord[0].seriesWinner(), NLSeriesRecord[0].seriesWinner()]);
      this.generateSeries(this.Weeks[cfWeek][1] as MlbWeek, dvTeams[0], dvTeams[1], cfWeek, 7, 2475, true);
      this.clearSeries((this.Weeks[cfWeek][1] as MlbWeek).Series()[0]);
    }
  }

  clearSeries(series: any): void {
    const self = this;
    for (const game of series.games()) {
      const _gameInput = game();
      const gameNum = _gameInput.gameScheduleNum();
      if (gameNum || gameNum === 0) {
        self.GameArray[gameNum] = SelectionType.NONE;
      } else {
        console.log(_gameInput);
      }
    }
    clearTimeout(self.Timeout);
    self.Timeout = setTimeout(() => {
      self.updateSaveString();
      self.pushHistory();
      super.finishComputing();
    }, 250);
  }

  selectGame(
    gameInput: KnockoutObservable<IGame>,
    selectedTeam: any,
    selectionType: SelectionType,
    isRandomize: boolean = false,
  ): void {
    const self = this;
    const _gameInput = gameInput();
    const home = (_gameInput as any).home();
    const away = (_gameInput as any).away();
    const input = {
      gameGuid: _gameInput.gameGuid,
      home: {
        Conference: home.Conference,
        Division: home.Division,
        TeamName: home.TeamName,
        Pct: home.GameRecords.AllRecord.Pct,
      },
      away: {
        Conference: away.Conference,
        Division: away.Division,
        TeamName: away.TeamName,
        Pct: away.GameRecords.AllRecord.Pct,
      },
    };
    self.GameArray[_gameInput.gameScheduleNum()] = selectionType;

    if (typeof selectedTeam === 'function' && selectedTeam().TeamAbbreviation === _gameInput.teamSelected()) {
      _gameInput.selectionMade(false);
      _gameInput.teamSelected('NONE');
      _gameInput.selectionType(SelectionType.NONE);
      home.Wins.remove((p: IGame) => p.gameGuid === input.gameGuid);
      away.Wins.remove((p: IGame) => p.gameGuid === input.gameGuid);
      self.GameArray[_gameInput.gameScheduleNum()] = 0;
      home.Losses.remove((p: IGame) => p.gameGuid === input.gameGuid);
      away.Losses.remove((p: IGame) => p.gameGuid === input.gameGuid);
    } else {
      _gameInput.selectionMade(true);
      _gameInput.selectionType(selectionType);

      if ((_gameInput as any).week <= self.MaxWeek()) {
        switch (selectionType) {
          case SelectionType.AWAY_WIN:
            _gameInput.teamSelected(away.TeamAbbreviation);
            away.Wins.push(input as unknown as IGame);
            home.Losses.push(input as unknown as IGame);
            break;
          case SelectionType.HOME_WIN:
            _gameInput.teamSelected(home.TeamAbbreviation);
            home.Wins.push(input as unknown as IGame);
            away.Losses.push(input as unknown as IGame);
            break;
          case SelectionType.NONE:
            _gameInput.teamSelected('NONE');
            break;
        }
      }
    }

    for (let i = self.NumGames() + 46; i < self.GameArray.length; i++) {
      self.GameScheduleGameMap[i] = null;
    }
    (self.Weeks[self.MaxWeek() + 1][1] as MlbWeek).Series([]);
    self.GameArray.splice(self.NumGames() + 47, self.GameArray.length);

    if ((_gameInput as any).week <= self.MaxWeek()) {
      clearTimeout(self.Timeout);
      self.Timeout = setTimeout(() => {
        self.UpdateRankings(Conferences.AL, true, -1);
        self.UpdateRankings(Conferences.NL, true, -1);
        super.finishComputing();
        self.updateSaveString();
        self.pushHistory();
      }, 400);
    }
  }

  selectSeriesGame(
    gameInput: KnockoutObservable<IGame>,
    selectedTeam: any,
    selectionType: SelectionType,
    isRandomize: boolean = false,
  ): void {
    const self = this;
    if (!gameInput || !(gameInput() as any).away || !(gameInput() as any).home) {
      return;
    }
    const _gameInput = gameInput();
    const home = (_gameInput as any).home();
    const away = (_gameInput as any).away();
    self.GameArray[_gameInput.gameScheduleNum()] = selectionType;

    if (typeof selectedTeam === 'function' && selectedTeam().TeamAbbreviation === _gameInput.teamSelected()) {
      _gameInput.selectionMade(false);
      _gameInput.teamSelected('NONE');
      _gameInput.selectionType(SelectionType.NONE);
      self.GameArray[_gameInput.gameScheduleNum()] = SelectionType.NONE;
    } else {
      _gameInput.selectionMade(selectionType !== SelectionType.NONE);
      _gameInput.selectionType(selectionType);
      switch (selectionType) {
        case SelectionType.AWAY_WIN:
          _gameInput.teamSelected(away.TeamAbbreviation);
          break;
        case SelectionType.HOME_WIN:
          _gameInput.teamSelected(home.TeamAbbreviation);
          break;
        case SelectionType.NONE:
          _gameInput.teamSelected('NONE');
          break;
      }
    }

    if ((_gameInput as any).week === self.MaxWeek() + 1) {
      self.seedDV();
    }
    if ((_gameInput as any).week === self.MaxWeek() + 2) {
      self.seedCG();
    }
    if ((_gameInput as any).week === self.MaxWeek() + 3) {
      self.seedFinals();
    }

    clearTimeout(self.Timeout);
    self.Timeout = setTimeout(() => {
      this.UpdateStatsPostSeason();
      super.finishComputing();
    }, 250);
  }

  gameSelected(
    gameInput: KnockoutObservable<IGame>,
    selectedTeam: any,
    selectionType: SelectionType,
    positionCounter?: number,
    skipUpdate?: boolean,
  ): void {
    const self = this;
    gameInput().teamSelected(selectedTeam().TeamAbbreviation);
    gameInput().selectionMade(true);
    gameInput().selectionType(selectionType);
    if (!skipUpdate) {
      self.UpdateStats(positionCounter, positionCounter === self.NumGames() - 1);
    }
  }

  UpdateRankings(conferenceName: string, fillWildCard: boolean, Position: number): void {
    const self = this;
    const teams: MlbTeamObs[] = [];
    const teamsInConference = this.Dict[conferenceName];
    let divWinners: MlbTeamObs[] = [];

    if (fillWildCard || Position === -1) {
      for (const _conference in teamsInConference) {
        const conference = teamsInConference[_conference];
        for (const _team in conference) {
          teams.push(conference[_team]);
        }
      }

      for (const team of teams) {
        team().Tiebreakers({
          HTH: [],
          Sweeps: [],
          DivHTH: [],
          Explanations: [],
        });
      }

      const divisions = Enumerable.From(teams).GroupBy((p: MlbTeamObs) => p().Division).ToArray();
      for (const division of divisions) {
        for (const divTeam of division.source) {
          divTeam().RankMethod(null);
          divTeam().IsDivisionWinner?.(false);
        }
        const winner = this.DivisionPickTop(division);
        divWinners.push(winner);
        let topWins = winner().GameRecords.AllRecord.Wins();
        let topLosses = winner().GameRecords.AllRecord.Losses();

        for (const idx in division.source) {
          const divTeam = division.source[idx];
          if (divTeam === winner) {
            winner().DivisionGamesBack(null);
            continue;
          } else {
            divTeam().DivisionGamesBack(this.CalcGamesBack(
              topWins, topLosses,
              divTeam().GameRecords.AllRecord.Wins(),
              divTeam().GameRecords.AllRecord.Losses(),
            ));
          }
          divTeam().Tiebreakers({ HTH: [], Sweeps: [], Explanations: [] });
        }

        const divGBOrder = Enumerable.From(division.source).OrderBy((p: any) => p().DivisionGamesBack()).ToArray();
        const realWinner = divGBOrder[0];
        topWins = realWinner().GameRecords.AllRecord.Wins();
        topLosses = realWinner().GameRecords.AllRecord.Losses();

        for (const idx in division.source) {
          const divTeam = division.source[idx];
          if (divTeam === realWinner) {
            realWinner().DivisionGamesBack(null);
          } else {
            divTeam().DivisionGamesBack(this.CalcGamesBack(
              topWins, topLosses,
              divTeam().GameRecords.AllRecord.Wins(),
              divTeam().GameRecords.AllRecord.Losses(),
            ));
          }
        }
      }

      for (const divWinner of divWinners) {
        const idx = teams.indexOf(divWinner);
        if (idx !== -1) {
          teams.splice(idx, 1);
        }
      }

      divWinners = this.ConferenceRanker(divWinners);
      let teamsSorted = this.ConferenceRanker(teams);
      teamsSorted = ([] as MlbTeamObs[]).concat.apply(divWinners, teamsSorted);
      teamsSorted = Enumerable.From(teamsSorted).Where((p: any) => p !== undefined).ToArray();
      ko.utils.arrayForEach(teamsSorted, (team: MlbTeamObs, index: number) => {
        team().ConferenceRank(index + 1);
        team().Tiebreakers.valueHasMutated();
      });

      self.seedWc(conferenceName, fillWildCard);
    }
  }

  seedWc(confName: string, fillWildCard: boolean): void {
    const self = this;
    const wcNum = confName === 'AL' ? self.NumGames() - 1 : self.NumGames() + 5;
    const wcSlot = confName === 'AL' ? 0 : 2;
    const topFunc = confName === 'AL' ? self.ALTop : self.NLTop;
    const teamsSorted = Enumerable.From(self.AllTeams())
      .Where((p: KnockoutObservable<any>) => p().Conference === confName)
      .OrderBy((p: KnockoutObservable<any>) => p().ConferenceRank())
      .ToArray();

    for (const series of (this.Weeks[self.MaxWeek() + 1][1] as MlbWeek).Series()) {
      for (const seriesGame of series.games()) {
        if (series.isPlayoffGame() && (seriesGame() as any).home().Conference === confName) {
          self.GameScheduleGameMap[(seriesGame() as any).gameScheduleNum()] = null;
          series.games().splice(series.games().indexOf(seriesGame), 1);
        }
      }
    }

    if ((self.Weeks[self.MaxWeek() + 1][1] as MlbWeek).Series().length === 0) {
      self.Warning('');
    }

    const topTeam: any = topFunc()[0]();
    const topWins = topTeam.GameRecords.AllRecord.Wins();
    const topLosses = topTeam.GameRecords.AllRecord.Losses();
    for (const team of teamsSorted as any[]) {
      const unwrapped = team();
      if (unwrapped === topTeam) {
        unwrapped.ConferenceGamesBack(null);
      } else {
        unwrapped.ConferenceGamesBack(this.CalcGamesBack(
          topWins, topLosses,
          unwrapped.GameRecords.AllRecord.Wins(),
          unwrapped.GameRecords.AllRecord.Losses(),
        ));
      }
    }

    const hasPlayoffGames = (this.Weeks[self.MaxWeek() + 1][1] as MlbWeek).Series().some(
      (p: any) => p.games().some((g: KnockoutObservable<IGame>) => (g() as any).home().Conference === confName),
    );

    if (fillWildCard && !hasPlayoffGames) {
      (this.Weeks[self.MaxWeek() + 1][wcSlot] as MlbWeek).Series([]);
      this.generateSeries(this.Weeks[self.MaxWeek() + 1][wcSlot] as MlbWeek, topFunc()[2], topFunc()[5], self.MaxWeek() + 1, 3, wcNum);
      this.generateSeries(this.Weeks[self.MaxWeek() + 1][wcSlot] as MlbWeek, topFunc()[3], topFunc()[4], self.MaxWeek() + 1, 3, wcNum + 3);

      (this.Weeks[self.MaxWeek() + 2][wcSlot] as MlbWeek).Series([]);
      (this.Weeks[self.MaxWeek() + 3][wcSlot] as MlbWeek).Series([]);
      this.clearSeries((this.Weeks[self.MaxWeek() + 1][wcSlot] as MlbWeek).Series()[0]);
    }
    if (hasPlayoffGames) {
      (this.Weeks[self.MaxWeek() + 1][wcSlot] as MlbWeek).Series([]);
    }
  }

  getHomeFieldAdvantageForPlayoffs(teams: any[]): MlbTeamObs[] {
    const team1 = teams[0];
    const team2 = teams[1];
    // LCS home field belongs to the higher postseason seed, even against a team with more wins.
    return team1().ConferenceRank() <= team2().ConferenceRank()
      ? [team1, team2]
      : [team2, team1];
  }

  getHomeFieldAdvantageForFinals(teams: any[]): MlbTeamObs[] {
    const team1 = teams[0];
    const team2 = teams[1];
    const first = team1();
    const second = team2();
    const firstRecord = first.GameRecords.AllRecord;
    const secondRecord = second.GameRecords.AllRecord;
    const firstPercentage = firstRecord.Pct();
    const secondPercentage = secondRecord.Pct();
    if (firstPercentage !== secondPercentage) {
      return firstPercentage > secondPercentage ? [team1, team2] : [team2, team1];
    }

    const headToHead = this.buildMlbGamesLookup([team1, team2])
      .getGamesBetween(first.TeamAbbreviation, second.TeamAbbreviation);
    const firstHeadToHeadWins = headToHead.filter(game => game.winner === first.TeamAbbreviation).length;
    const secondHeadToHeadWins = headToHead.filter(game => game.winner === second.TeamAbbreviation).length;
    if (firstHeadToHeadWins !== secondHeadToHeadWins) {
      return firstHeadToHeadWins > secondHeadToHeadWins ? [team1, team2] : [team2, team1];
    }

    const firstDivisionPercentage = first.GameRecords.DivisionRecord.Pct();
    const secondDivisionPercentage = second.GameRecords.DivisionRecord.Pct();
    if (firstDivisionPercentage !== secondDivisionPercentage) {
      return firstDivisionPercentage > secondDivisionPercentage ? [team1, team2] : [team2, team1];
    }

    const firstLeaguePercentage = first.GameRecords.ConferenceRecord.Pct();
    const secondLeaguePercentage = second.GameRecords.ConferenceRecord.Pct();
    if (firstLeaguePercentage !== secondLeaguePercentage) {
      return firstLeaguePercentage > secondLeaguePercentage ? [team1, team2] : [team2, team1];
    }

    // The remaining MLB tiebreakers use last-half intraleague game order, which is not tracked here.
    return [team1, team2];
  }

  generateSeries(
    week: MlbWeek,
    team1: MlbTeamObs,
    team2: MlbTeamObs,
    weekNum: number,
    numGames: number,
    gameScheduleNum: number,
    preventReRank?: boolean,
  ): void {
    const self = this;
    const games: KnockoutObservable<IGame>[] = [];
    const home = team1;
    const away = team2;

    const game1 = ko.observable(new MlbGame(home, away, weekNum, gameScheduleNum + 1, null, null, weekNum + '1'));
    this.GameScheduleGameMap[gameScheduleNum + 1] = game1;
    games.push(game1);

    for (const series of week.Series()) {
      if (series.games().length === 0) {
        week.Series().splice(week.Series().indexOf(series), 1);
      }
    }

    if (numGames === 1) {
      const series = new Series(games);
      if (gameScheduleNum >= self.NumGames()) {
        if (this.GameArray.length < gameScheduleNum) {
          for (let i = this.GameArray.length; i < gameScheduleNum + 1; i++) {
            this.GameArray[i] = 0;
          }
        }
        series.isPlayoffGame(true);
      }
      week.Series.push(series);
      return;
    } else if (numGames === 3) {
      const game2 = ko.observable(new MlbGame(home, away, weekNum, gameScheduleNum + 2, null, null, weekNum + '2'));
      const game3 = ko.observable(new MlbGame(home, away, weekNum, gameScheduleNum + 3, null, null, weekNum + '3'));
      this.GameScheduleGameMap[gameScheduleNum + 2] = game2;
      this.GameScheduleGameMap[gameScheduleNum + 3] = game3;
      games.push(game2);
      games.push(game3);
      this.GameArray[gameScheduleNum + 1] = 0;
      this.GameArray[gameScheduleNum + 2] = 0;
    } else if (numGames === 5) {
      const game2 = ko.observable(new MlbGame(home, away, weekNum, gameScheduleNum + 2, null, null, weekNum + '2'));
      const game3 = ko.observable(new MlbGame(away, home, weekNum, gameScheduleNum + 3, null, null, weekNum + '3'));
      const game4 = ko.observable(new MlbGame(away, home, weekNum, gameScheduleNum + 4, null, null, weekNum + '4'));
      const game5 = ko.observable(new MlbGame(home, away, weekNum, gameScheduleNum + 5, null, null, weekNum + '5'));
      this.GameScheduleGameMap[gameScheduleNum + 2] = game2;
      this.GameScheduleGameMap[gameScheduleNum + 3] = game3;
      this.GameScheduleGameMap[gameScheduleNum + 4] = game4;
      this.GameScheduleGameMap[gameScheduleNum + 5] = game5;
      games.push(game2);
      games.push(game3);
      games.push(game4);
      games.push(game5);
      this.GameArray[gameScheduleNum + 1] = 0;
      this.GameArray[gameScheduleNum + 2] = 0;
      this.GameArray[gameScheduleNum + 3] = 0;
      this.GameArray[gameScheduleNum + 4] = 0;
      this.GameArray[gameScheduleNum + 5] = 0;
    }

    if (numGames === 7) {
      const game2 = ko.observable(new MlbGame(home, away, weekNum, gameScheduleNum + 2, null, null, weekNum + '2'));
      const game3 = ko.observable(new MlbGame(away, home, weekNum, gameScheduleNum + 3, null, null, weekNum + '3'));
      const game4 = ko.observable(new MlbGame(away, home, weekNum, gameScheduleNum + 4, null, null, weekNum + '4'));
      const game5 = ko.observable(new MlbGame(away, home, weekNum, gameScheduleNum + 5, null, null, weekNum + '5'));
      this.GameScheduleGameMap[gameScheduleNum + 2] = game2;
      this.GameScheduleGameMap[gameScheduleNum + 3] = game3;
      this.GameScheduleGameMap[gameScheduleNum + 4] = game4;
      this.GameScheduleGameMap[gameScheduleNum + 5] = game5;
      games.push(game2);
      games.push(game3);
      games.push(game4);
      games.push(game5);
      this.GameArray[gameScheduleNum + 1] = 0;
      this.GameArray[gameScheduleNum + 2] = 0;
      this.GameArray[gameScheduleNum + 3] = 0;
      this.GameArray[gameScheduleNum + 4] = 0;
      this.GameArray[gameScheduleNum + 5] = 0;
      const game6 = ko.observable(new MlbGame(home, away, weekNum, gameScheduleNum + 6, null, null, weekNum + '6'));
      const game7 = ko.observable(new MlbGame(home, away, weekNum, gameScheduleNum + 7, null, null, weekNum + '7'));
      this.GameScheduleGameMap[gameScheduleNum + 6] = game6;
      this.GameScheduleGameMap[gameScheduleNum + 7] = game7;
      games.push(game6);
      games.push(game7);
    }

    this.GameArray[gameScheduleNum + 6] = 0;
    this.GameArray[gameScheduleNum + 7] = 0;
    week.Series.push(new Series(games));
  }

  DivisionPickTop(teamsInADivision: { source: MlbTeamObs[] }): MlbTeamObs {
    const divTeams = teamsInADivision.source;
    const teamObsMap = buildTeamObsMap(divTeams) as Map<string, MlbTeamObs>;

    // Within a single division, no team is yet a division winner.
    const plainTeams: MlbTiebreakerTeam[] = divTeams.map(obs => toMlbTiebreakerTeam(obs, false));
    const gamesLookup = this.buildMlbGamesLookup(divTeams);

    // Run service to get ranked order.
    const ranked = mlbConferenceRanker(plainTeams, gamesLookup);
    const orderedPlain = ranked.orderedTeams;
    if (orderedPlain.length === 0) {
      // Should not occur; fall back to first team.
      return divTeams[0];
    }

    const winnerPlain = orderedPlain[0];
    const winnerObs = teamObsMap.get(winnerPlain.teamAbbreviation)!;

    // Determine the RankMethod assigned by the service to the winner.
    const serviceRank = ranked.rankMethods.get(winnerPlain.teamAbbreviation);
    const isCoinFlip = serviceRank === RankMethod.CoinFlip;

    // Override rank with Division-Winner semantics expected by UI.
    if (isCoinFlip) {
      winnerObs().RankMethod(RankMethod.DivisionWinnerCoinFlip);
      winnerObs().IsDivisionWinner?.(true);
    } else {
      winnerObs().RankMethod(RankMethod.DivisionWinner);
      winnerObs().IsDivisionWinner?.(true);
    }

    // Append user-visible explanations preserving legacy prose.
    const tiebreakers = winnerObs().Tiebreakers();
    if (tiebreakers) {
      if (isCoinFlip) {
        tiebreakers.Explanations.push('<strong>WARNING:</strong> Division winner was decided by coin flip');
      } else if (divTeams.length > 1) {
        const winnerAbbr = winnerObs().TeamAbbreviation;
        const winnerReason = winnerObs().RankMethodText();
        const losers = divTeams
          .filter(o => o().TeamAbbreviation !== winnerAbbr)
          .map(o => o().TeamAbbreviation)
          .join(', ');
        tiebreakers.Explanations.push(
          `${winnerAbbr} defeats (${losers}) for division winner based on (${winnerReason})<hr/>`,
        );
      }
      winnerObs().Tiebreakers.valueHasMutated();
    }

    return winnerObs;
  }

  /**
   * Build a GameResultsLookup from the schedules of the given MLB team observables.
   * Shared by DivisionPickTop and ConferenceRanker.
   */
  private buildMlbGamesLookup(teams: MlbTeamObs[]): ArrayGameResultsLookup {
    const gameResults: GameResult[] = [];
    const seenGames = new Set<string>();
    for (const obs of teams) {
      const schedule = this.getScheduleForTeam(obs());
      for (const gameObs of schedule) {
        if (!gameObs) continue;
        const game = gameObs();
        if (!game || !game.selectionMade?.()) continue;
        const home = game.home?.();
        const away = game.away?.();
        if (!home || !away) continue;
        const gameKey = `${game.gameGuid ?? `${home.TeamAbbreviation}-${away.TeamAbbreviation}-${game.SortOrder?.()}`}`;
        if (seenGames.has(gameKey)) continue;
        seenGames.add(gameKey);
        const winner = game.TeamThatWon?.()?.()?.TeamAbbreviation ?? null;
        gameResults.push({
          homeTeam: home.TeamAbbreviation,
          awayTeam: away.TeamAbbreviation,
          winner,
          selectionType: game.selectionType?.() ?? 0,
        });
      }
    }
    return new ArrayGameResultsLookup(gameResults);
  }


  ConferenceRanker(teams: MlbTeamObs[], preventRankOverride?: boolean): MlbTeamObs[] {
    if (Enumerable.From(teams).Any((p: any) => p === undefined)) {
      return [];
    }

    // Build KO observable map
    const teamObsMap = new Map<string, MlbTeamObs>();
    for (const obs of teams) teamObsMap.set(obs().TeamAbbreviation, obs);

    // Convert KO teams to plain tiebreaker objects
    // isDivisionWinner is set based on the current RankMethod (already computed by DivisionPickTop)
    const plainTeams: MlbTiebreakerTeam[] = teams.map(obs => {
      const t = obs();
      const gr = t.GameRecords as any;
      return {
        teamAbbreviation: t.TeamAbbreviation,
        teamId: t.TeamID,
        teamName: t.TeamName,
        division: t.Division,
        conference: t.Conference,
        records: {
          all: { wins: gr.AllRecord.Wins(), losses: gr.AllRecord.Losses(), ties: gr.AllRecord.Ties(),
                 pct: calcPct([gr.AllRecord.Wins(), gr.AllRecord.Losses(), gr.AllRecord.Ties()]),
                 pctNaN: calcPctNaN([gr.AllRecord.Wins(), gr.AllRecord.Losses(), gr.AllRecord.Ties()]) },
          division: { wins: gr.DivisionRecord.Wins(), losses: gr.DivisionRecord.Losses(), ties: gr.DivisionRecord.Ties(),
                      pct: calcPct([gr.DivisionRecord.Wins(), gr.DivisionRecord.Losses(), gr.DivisionRecord.Ties()]),
                      pctNaN: calcPctNaN([gr.DivisionRecord.Wins(), gr.DivisionRecord.Losses(), gr.DivisionRecord.Ties()]) },
          conference: { wins: gr.ConferenceRecord.Wins(), losses: gr.ConferenceRecord.Losses(), ties: gr.ConferenceRecord.Ties(),
                        pct: calcPct([gr.ConferenceRecord.Wins(), gr.ConferenceRecord.Losses(), gr.ConferenceRecord.Ties()]),
                        pctNaN: calcPctNaN([gr.ConferenceRecord.Wins(), gr.ConferenceRecord.Losses(), gr.ConferenceRecord.Ties()]) },
        },
        isDivisionWinner: getMlbDivisionWinnerState(t),
      };
    });

    // Build game results lookup from team schedules (for H2H)
    const gameResults: GameResult[] = [];
    const seenGames = new Set<string>();
    for (const obs of teams) {
      const schedule = this.getScheduleForTeam(obs());
      for (const gameObs of schedule) {
        if (!gameObs) continue;
        const game = gameObs();
        if (!game || !game.selectionMade?.()) continue;
        const home = game.home?.();
        const away = game.away?.();
        if (!home || !away) continue;
        const gameKey = `${game.gameGuid ?? `${home.TeamAbbreviation}-${away.TeamAbbreviation}-${game.SortOrder?.()}`}`;
        if (seenGames.has(gameKey)) continue;
        seenGames.add(gameKey);
        const winner = game.TeamThatWon?.()?.()?.TeamAbbreviation ?? null;
        gameResults.push({
          homeTeam: home.TeamAbbreviation,
          awayTeam: away.TeamAbbreviation,
          winner,
          selectionType: game.selectionType?.() ?? 0,
        });
      }
    }
    const gamesLookup = new ArrayGameResultsLookup(gameResults);

    // Run extracted tiebreaker service (includes restart + N-team sweep bug fixes)
    const result = mlbConferenceRanker(plainTeams, gamesLookup);

    // Apply results back to KO observables
    if (!preventRankOverride) {
      for (const [abbr, rankMethod] of result.rankMethods) {
        const obs = teamObsMap.get(abbr);
        if (obs) obs().RankMethod(rankMethod);
      }
    }
    for (const [abbr, explanations] of result.explanations) {
      const obs = teamObsMap.get(abbr);
      if (obs) {
        const tiebreakers = obs().Tiebreakers();
        if (tiebreakers) {
          tiebreakers.Explanations = explanations;
          obs().Tiebreakers.valueHasMutated();
        }
      }
    }

    // Return KO observables in result order
    return result.orderedTeams
      .map(t => teamObsMap.get(t.teamAbbreviation))
      .filter((obs): obs is MlbTeamObs => obs != null);
  }

  ConferencePickTop(teams: MlbTeamObs[], preventRankOverride?: boolean): MlbTeamObs | undefined {
    if (Enumerable.From(teams).Any((p: any) => p === undefined)) {
      return undefined;
    }
    const ranked = this.ConferenceRanker(teams, preventRankOverride);
    return ranked[0];
  }

  UpdateStats(Position: number, fillWildcard?: boolean): void {
    const self = this;
    self.updateSaveString();
    self.UpdateRankings(Conferences.AL, fillWildcard, Position);
    self.UpdateRankings(Conferences.NL, fillWildcard, Position);
    self.pushHistory();
    document.querySelectorAll('#content .hidden').forEach(el => el.classList.remove('hidden'));
  }

  pushHistory(): void {
    const self = this;
    const maxWeek = self.MaxWeek();
    BracketGenerator.GenerateBracket(
      self.Weeks[maxWeek + 1],
      self.Weeks[maxWeek + 2],
      self.Weeks[maxWeek + 3],
      self.Weeks[maxWeek + 4],
    );
    super.pushHistory();
  }

  UpdateStatsPostSeason(): void {
    const self = this;
    self.updateSaveString();
    self.pushHistory();
    super.finishComputing();
  }

  resetWeek(): void {
    const self = this;
    for (const week of self.SelectedWeek().value) {
      for (const gameObs of (week as MlbWeek).games()) {
        const game = gameObs() as any;
        if (game.selectionMade()) {
          if (game.selectionType() !== SelectionType.TIE) {
            self.selectGame(gameObs, game.TeamThatWon(), SelectionType.NONE, true);
          } else {
            self.selectGame(gameObs, 'TIE', SelectionType.NONE, true);
          }
        }
      }
      for (const series of (week as MlbWeek).Series()) {
        for (const gameObs of series.games()) {
          self.unselectGame(gameObs, true);
        }
      }
    }
    self.UpdateStats(-1, self.SelectedWeek().key <= self.MaxWeek());
  }

  randomize(): void {
    const self = this;
    if (confirm('All of your regular season selections will be randomized, replacing your current selections. Are you sure you want to proceed?')) {
      for (let i = 0; i < self.NumGames(); i++) {
        const selection = Math.floor(Math.random() * 2) + 1;
        self.GameArray[i] = selection;
      }
      for (let i = self.NumGames(); i < self.NumGames() + 84; i++) {
        self.GameArray[i] = 0;
      }
      self.updateSaveString();
      self.load(self.GameArray.join(''));
      for (const team of self.AllTeams()) {
        team().FakeObs.valueHasMutated();
        team().Wins.valueHasMutated();
        team().Losses.valueHasMutated();
        team().Ties.valueHasMutated();
      }
      self.SelectedWeek({ key: self.MaxWeek(), value: self.Weeks[self.MaxWeek()] });
    }
  }

  randomizeWeek(): void {
    const self = this;
    for (const week of self.SelectedWeek().value) {
      for (const gameObs of (week as MlbWeek).games()) {
        const game = gameObs() as any;
        self.unselectGame(gameObs, true);
        const selection = Math.floor(Math.random() * 2) + 1;
        let teamSelected: any;
        switch (selection) {
          case SelectionType.AWAY_WIN:
            teamSelected = game.away;
            break;
          case SelectionType.HOME_WIN:
            teamSelected = game.home;
            break;
          default:
            teamSelected = game.home;
            break;
        }
        self.selectGame(gameObs, teamSelected, selection, true);
        game.TeamThatWon()().GameRecords.valueHasMutated();
        game.TeamThatLost()().GameRecords.valueHasMutated();
      }
      for (const series of (week as MlbWeek).Series()) {
        series.reset();
      }
      for (const series of (week as MlbWeek).Series()) {
        for (const gameObs of series.games()) {
          if (series.seriesDecided()) {
            self.selectSeriesGame(gameObs, 'NONE', 0, true);
            continue;
          }
          const game = gameObs() as any;
          self.unselectGame(gameObs, true);
          const selection = Math.floor(Math.random() * 2) + 1;
          let teamSelected: any;
          switch (selection) {
            case SelectionType.AWAY_WIN:
              teamSelected = game.away;
              break;
            case SelectionType.HOME_WIN:
              teamSelected = game.home;
              break;
            default:
              teamSelected = game.home;
              break;
          }
          self.selectSeriesGame(gameObs, teamSelected, selection, true);
        }
        series.games.valueHasMutated();
        series.seriesDecided();
      }
    }
    self.UpdateStats(-1, self.SelectedWeek().key <= self.MaxWeek());
  }
}
